import "./mobile-navbar.css";

import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { chevronDown, menu_01 } from "@tql/ui-components-react/untitled/icons";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

import { routes } from "~/app/routes/web-routes.ts";
import { TQLGlobeSVG } from "~/features/ftl/ftl-quotes/create-quote/components/tql-globe/tql-globe-svg.tsx";
import { ProfileSwitcher } from "~/shared/components/global-header/profile-switcher.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useGlobalSpinner } from "~/shared/hooks/use-global-spinner.ts";
import {
	availableCustomersAtom,
	selectedCustomerAtom,
} from "~/shared/state/user-profile-state.ts";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";

import { drawerExpandedAtom } from "../../state/navbar-state.ts";

export function MobileNavbar() {
	const [expanded, setExpanded] = useAtom(drawerExpandedAtom);
	const availableCustomers = useAtomValue(availableCustomersAtom);
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const location = useLocation();
	const { isInternal } = useDeploymentType();
	const {
		isEnabled: isCustomerProfileSwitchEnabled,
		isReady: isCustomerProfileSwitchReady,
	} = useFeatureFlagGate("enableCustomerProfileSwitch");
	const [isProfileOpen, setIsProfileOpen] = useState(false);
	const profileContainerRef = useRef<HTMLDivElement>(null);
	const { isLoading } = useGlobalSpinner();

	const hasMultipleCustomers = availableCustomers.length > 1;
	const shouldShowProfileSelector =
		!isInternal &&
		hasMultipleCustomers &&
		selectedCustomer !== null &&
		isCustomerProfileSwitchReady &&
		isCustomerProfileSwitchEnabled;

	const handleProfileToggle = useCallback(() => {
		setIsProfileOpen((prev) => !prev);
	}, []);

	const handleProfileClose = useCallback(() => {
		setIsProfileOpen(false);
	}, []);

	useEffect(() => {
		if (!isProfileOpen) return;

		const handleMouseDown = (event: MouseEvent) => {
			// Keep the switcher mounted while a global spinner is active (e.g. an
			// in-flight profile save). The save and its result handling — including
			// the error modal — live in ProfileSwitcher, so unmounting it mid-save
			// would tear down the loading overlay and drop the error feedback.
			if (isLoading) return;
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (profileContainerRef.current?.contains(target)) return;
			// Ignore clicks inside the Kendo Dialog portal (e.g. confirmation modal)
			if (
				target instanceof Element &&
				target.closest(".k-dialog-wrapper, .k-animation-container")
			) {
				return;
			}
			setIsProfileOpen(false);
		};

		document.addEventListener("mousedown", handleMouseDown);
		return () => {
			document.removeEventListener("mousedown", handleMouseDown);
		};
	}, [isProfileOpen, isLoading]);

	const ftlHeaderBackground =
		location.pathname === routes.createFTLQuote ||
		location.pathname === routes.ftlShipment;

	return (
		<div
			className={ftlHeaderBackground ? "mobile-navbar-ftl" : "mobile-navbar"}
		>
			<div className={"mobile-navbar-menu-button"}>
				<Button
					aria-expanded={expanded}
					aria-label={
						expanded ? "Close navigation menu" : "Open navigation menu"
					}
					onClick={() => setExpanded(!expanded)}
					variant={"tertiary"}
				>
					<SvgIcon icon={menu_01} size={"xlarge"} />
				</Button>
			</div>
			{shouldShowProfileSelector ? (
				<div
					ref={profileContainerRef}
					className={"mobile-navbar-profile-container"}
				>
					<div className={"mobile-navbar-profile-trigger"}>
						<Button
							aria-expanded={isProfileOpen}
							aria-label={`Change active company. Current company: ${selectedCustomer.name}`}
							data-testid={"mobile-company-selector"}
							onClick={handleProfileToggle}
							variant={"tertiary"}
						>
							<span className={"mobile-navbar-company-name"}>
								{selectedCustomer.name}
							</span>
							<SvgIcon icon={chevronDown} size={"small"} />
						</Button>
					</div>
					<span
						aria-hidden={"true"}
						className={"mobile-navbar-standalone-globe"}
						data-testid={"mobile-standalone-globe"}
					>
						<TQLGlobeSVG />
					</span>
					{isProfileOpen ? (
						<ProfileSwitcher onClose={handleProfileClose} />
					) : null}
				</div>
			) : (
				<>
					<img
						alt={"TQL TRAX"}
						className={"mobile-navbar-logo-lockup"}
						data-testid={"mobile-trax-logo"}
						src={`${getBaseUrl()}/images/tql-trax-logo-white.png`}
					/>
					<span className={"mobile-navbar-right-spacer"} />
				</>
			)}
		</div>
	);
}
